package fr.manyverse.app.presentation.components

import androidx.compose.foundation.gestures.detectDragGestures
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxScope
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.layout.onSizeChanged
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.IntSize
import androidx.compose.ui.unit.dp
import kotlin.math.roundToInt

/**
 * A floating bubble anchored to a bottom corner that the user can drag freely,
 * with the drag offset clamped so the bubble never leaves the container bounds.
 *
 * The bubble's natural position is given by [alignment] + [bottomPadding] + [sidePadding];
 * [bottomPadding] is computed by the caller so multiple bubbles can be stacked
 * (e.g. `(100 + index * 60).dp`).
 *
 * [dragKey] should be a stable identity (e.g. an entity id) so the drag gesture
 * is reset when the underlying bubble changes.
 */
@Composable
fun BoxScope.DraggableBubble(
    alignment: Alignment,
    bottomPadding: Dp,
    containerWidth: Int,
    containerHeight: Int,
    dragKey: Any?,
    sidePadding: Dp = 16.dp,
    content: @Composable () -> Unit
) {
    var offset by remember { mutableStateOf(Offset.Zero) }
    var size by remember { mutableStateOf(IntSize.Zero) }
    val isStart = alignment == Alignment.BottomStart
    Box(
        modifier = Modifier
            .align(alignment)
            .padding(
                start = if (isStart) sidePadding else 0.dp,
                end = if (isStart) 0.dp else sidePadding,
                bottom = bottomPadding
            )
            .onSizeChanged { size = it }
            .offset { IntOffset(offset.x.roundToInt(), offset.y.roundToInt()) }
            .pointerInput(dragKey, containerWidth, containerHeight) {
                val side = sidePadding.roundToPx()
                val bottom = bottomPadding.roundToPx()
                detectDragGestures { _, delta ->
                    offset = if (size == IntSize.Zero) offset + delta
                    else clampBubbleOffset(
                        proposed = offset + delta,
                        naturalX = if (isStart) side else containerWidth - size.width - side,
                        naturalY = containerHeight - size.height - bottom,
                        bubbleW = size.width,
                        bubbleH = size.height,
                        containerW = containerWidth,
                        containerH = containerHeight
                    )
                }
            }
    ) {
        content()
    }
}

/**
 * Clamps a proposed drag [offset] so a bubble of size [bubbleW]x[bubbleH], whose
 * natural (un-dragged) top-left is at ([naturalX], [naturalY]), stays fully inside
 * a container of size [containerW]x[containerH].
 */
internal fun clampBubbleOffset(
    proposed: Offset,
    naturalX: Int,
    naturalY: Int,
    bubbleW: Int,
    bubbleH: Int,
    containerW: Int,
    containerH: Int
): Offset = Offset(
    x = proposed.x.coerceIn(-naturalX.toFloat(), (containerW - naturalX - bubbleW).toFloat()),
    y = proposed.y.coerceIn(-naturalY.toFloat(), (containerH - naturalY - bubbleH).toFloat())
)
